import { useMemo, useState } from "react";
import { toast } from "sonner";
import client from "@/lib/api";
import { Card } from "@/components/shared/UI";
import { CollabIcon, collabTypeCls, collabDates, COLLAB_STATUSES, ALL_COLLAB_STATUSES, COLLAB_TYPE_LABEL } from "@/lib/collabs";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

const COL_DOT = {
  Prospect: "bg-slate-400", "In Discussion": "bg-sky-400", "Confirmed/Scheduled": "bg-indigo-400", "In Progress": "bg-emerald-400",
  Active: "bg-emerald-400", Completed: "bg-slate-300", Ended: "bg-slate-300", Declined: "bg-red-300", Cancelled: "bg-red-300",
};

// Pipeline board by status; drag a card to another column to change its status
export function CollabBoard({ items, type, onOpen, onChanged }) {
  const [over, setOver] = useState(null);
  const cols = type ? COLLAB_STATUSES[type] : ALL_COLLAB_STATUSES;
  const drop = async (status, id) => {
    setOver(null);
    const c = items.find((x) => x.id === id);
    if (!c || c.status === status) return;
    if (!(COLLAB_STATUSES[c.collab_type] || []).includes(status)) return toast.error(`"${status}" isn't a status for ${COLLAB_TYPE_LABEL[c.collab_type]}s`);
    try {
      await client.put(`/collaborations/${c.id}`, { ...c, status });
      onChanged();
    } catch (e) { toast.error("Could not update status"); }
  };
  return (
    <div className="flex gap-3 overflow-x-auto pb-2" data-testid="collabs-board">
      {cols.map((s) => {
        const rows = items.filter((c) => c.status === s);
        return (
          <div key={s} data-testid={`collabs-board-col-${s.replace(/[\s/]/g, "-").toLowerCase()}`}
            onDragOver={(e) => { e.preventDefault(); setOver(s); }} onDragLeave={() => setOver((o) => (o === s ? null : o))}
            onDrop={(e) => drop(s, e.dataTransfer.getData("text/collab"))}
            className={cn("w-64 shrink-0 rounded-xl bg-slate-50 p-2.5 transition-colors", over === s && "bg-sky-50 ring-2 ring-sky-200")}>
            <div className="mb-2 flex items-center gap-2 px-1">
              <span className={cn("h-2 w-2 rounded-full", COL_DOT[s])} />
              <span className="text-sm font-semibold text-slate-700">{s}</span>
              <span className="ml-auto rounded-full bg-white px-2 text-[11px] font-bold text-slate-500">{rows.length}</span>
            </div>
            <div className="min-h-[60px] space-y-2">
              {rows.map((c) => (
                <button key={c.id} draggable onDragStart={(e) => e.dataTransfer.setData("text/collab", c.id)} onClick={() => onOpen(c)}
                  data-testid={`collabs-board-card-${c.id}`} className="w-full cursor-grab rounded-lg border border-slate-100 bg-white p-2.5 text-left shadow-sm transition-colors hover:border-sky-200 active:cursor-grabbing">
                  <div className="flex items-start gap-2">
                    <span className={cn("flex h-6 w-6 shrink-0 items-center justify-center rounded-md", collabTypeCls(c.collab_type))}><CollabIcon type={c.collab_type} className="h-3.5 w-3.5" /></span>
                    <p className="min-w-0 flex-1 text-sm font-medium leading-snug text-slate-800">{c.title}</p>
                  </div>
                  <p className="mt-1.5 text-[11px] text-slate-400">{collabDates(c)}</p>
                  {c.property_names?.length > 0 && <p className="truncate text-[11px] text-slate-500">{c.property_names.join(", ")}</p>}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const key = (d) => d.toLocaleDateString("en-CA");

const MAX_LANES = 4;

// Split items into per-week bar segments with stacked lanes so multi-day items render as one connected bar
function weekSegments(items, weekDays, lastKey) {
  const wk = weekDays.map(key);
  const segs = [];
  items.forEach((c) => {
    const s = (c.start_date || c.end_date || "").slice(0, 10);
    if (!s) return;
    const e = c.collab_type === "Ongoing" && c.ongoing ? lastKey : (c.end_date || c.start_date).slice(0, 10);
    if (e < wk[0] || s > wk[6]) return;
    const startCol = Math.max(0, wk.findIndex((k) => k >= s));
    let endCol = 6;
    for (let i = 6; i >= 0; i -= 1) if (wk[i] <= e) { endCol = i; break; }
    segs.push({ c, startCol, endCol, contLeft: s < wk[0], contRight: e > wk[6] });
  });
  segs.sort((a, b) => a.startCol - b.startCol || (b.endCol - b.startCol) - (a.endCol - a.startCol));
  const lanes = [];
  segs.forEach((g) => {
    let lane = lanes.findIndex((end) => end < g.startCol);
    if (lane === -1) { lane = lanes.length; lanes.push(-1); }
    lanes[lane] = g.endCol;
    g.lane = lane;
  });
  return segs;
}

export function CollabCalendar({ items, onOpen }) {
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const weeks = useMemo(() => {
    const start = new Date(month); start.setDate(1 - ((month.getDay() + 6) % 7));
    const days = Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
    return [0, 1, 2, 3, 4, 5].map((w) => days.slice(w * 7, w * 7 + 7));
  }, [month]);
  const today = key(new Date());
  const lastKey = key(weeks[5][6]);
  const shift = (n) => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + n, 1));
  return (
    <Card className="overflow-hidden" data-testid="collabs-calendar">
      <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3">
        <button onClick={() => shift(-1)} data-testid="collabs-cal-prev" className="rounded-md p-1 text-slate-500 hover:bg-slate-100"><ChevronLeft className="h-4 w-4" /></button>
        <span className="min-w-[140px] text-center font-heading text-sm font-semibold text-slate-800" data-testid="collabs-cal-month">{month.toLocaleDateString("en-AU", { month: "long", year: "numeric" })}</span>
        <button onClick={() => shift(1)} data-testid="collabs-cal-next" className="rounded-md p-1 text-slate-500 hover:bg-slate-100"><ChevronRight className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-7 border-b border-slate-100 text-center text-[11px] font-semibold uppercase text-slate-400">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="py-1.5">{d}</div>)}
      </div>
      {weeks.map((wd, wi) => {
        const segs = weekSegments(items, wd, lastKey);
        const shown = segs.filter((g) => g.lane < MAX_LANES);
        const hiddenPerDay = wd.map((_, i) => segs.filter((g) => g.lane >= MAX_LANES && g.startCol <= i && g.endCol >= i).length);
        return (
          <div key={wi} className="relative border-b border-slate-50" data-testid={`collabs-cal-week-${wi}`}>
            <div className="grid grid-cols-7">
              {wd.map((d, i) => {
                const k = key(d);
                const inMonth = d.getMonth() === month.getMonth();
                return (
                  <div key={k} data-testid={`collabs-cal-day-${k}`} className={cn("min-h-[112px] border-r border-slate-50 p-1.5", !inMonth && "bg-slate-50/50")}>
                    <span className={cn("inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px]", k === today ? "bg-sky-600 font-bold text-white" : inMonth ? "text-slate-600" : "text-slate-300")}>{d.getDate()}</span>
                    {hiddenPerDay[i] > 0 && <span className="absolute bottom-1 px-1 text-[10px] text-slate-400" style={{ left: `calc(${(i / 7) * 100}% + 4px)` }}>+{hiddenPerDay[i]} more</span>}
                  </div>
                );
              })}
            </div>
            <div className="pointer-events-none absolute inset-x-0 top-8 grid grid-cols-7 gap-y-0.5" style={{ gridAutoRows: "20px" }}>
              {shown.map((g) => (
                <button key={g.c.id} onClick={() => onOpen(g.c)} title={`${COLLAB_TYPE_LABEL[g.c.collab_type]}: ${g.c.title} (${g.c.status})`}
                  data-testid={`collabs-cal-bar-${g.c.id}-w${wi}`}
                  style={{ gridColumn: `${g.startCol + 1} / ${g.endCol + 2}`, gridRow: g.lane + 1 }}
                  className={cn("pointer-events-auto flex items-center gap-1 truncate px-1.5 text-left text-[11px] font-medium transition-opacity hover:opacity-80",
                    collabTypeCls(g.c.collab_type), g.contLeft ? "ml-0 rounded-l-none" : "ml-1 rounded-l", g.contRight ? "mr-0 rounded-r-none" : "mr-1 rounded-r")}>
                  {!g.contLeft && <CollabIcon type={g.c.collab_type} className="h-3 w-3 shrink-0" />}
                  <span className="truncate">{g.c.title}</span>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </Card>
  );
}
